<script setup lang="ts">
import { useLanguage } from '#composables/useLanguage';
import { sanitizeValueToI18nKey } from '@carrefour-gcs/shared';
import { type Row } from '@tanstack/vue-table';
import { computed } from 'vue';
import type { DataTableCellFormattedType, DataTableRowType } from '../dataTable.model';
import { isCustomerSegmentationColumn } from '../dataTable.utils';
import ContentCellContainer from './ContentCellContainer.vue';
import type { ContentCellContainerPropsType } from './types/contentCellContainerProps.type';

const props = defineProps<
  ContentCellContainerPropsType & {
    cell: DataTableCellFormattedType;
    columnId: string;
    row?: Row<DataTableRowType<string>>;
  }
>();

const { t } = useLanguage();

const isEmpty = computed(
  () =>
    !props.cell.data &&
    (props.cell.type === 'dimension' || (!props.cell.variationScore && !props.cell.variationScoreBenchmark))
);

const variationScoreClass = computed(() => ({
  'text-success': props.cell.type === 'kpi' && props.cell.variationScoreType === 'positive',
  'text-error': props.cell.type === 'kpi' && props.cell.variationScoreType === 'negative'
}));

const dimensionScoreClass = computed(() => ({
  'text-center': isEmpty.value,
  'text-left': !isEmpty.value,
  'text-persistent-content-functional-main-information':
    props.columnId === 'customer_movement_type' && props.cell.data === 'Regular',
  'text-persistent-content-functional-main-positive':
    props.columnId === 'customer_movement_type' && props.cell.data === 'New',
  'text-persistent-content-functional-main-negative':
    props.columnId === 'customer_movement_type' && props.cell.data === 'Lost'
}));
const dimensionSpanClass = computed(() => ({
  'bg-background-subtle-hover': props.columnId === 'customer_movement_type' && props.cell.data === 'Regular',
  'bg-background-functional-reversed-success': props.columnId === 'customer_movement_type' && props.cell.data === 'New',
  'bg-background-functional-reversed-negative':
    props.columnId === 'customer_movement_type' && props.cell.data === 'Lost'
}));

const formattedDimensionsTextValues = computed(() => {
  if (props.columnId === 'customer_movement_type' && props.cell.data) {
    return t(`dataTablePerformance.dimension.movementType.${sanitizeValueToI18nKey(props.cell.data)}`);
  }

  if (props.columnId === 'customer_movement_type_detailed_key' && props.cell.data) {
    return t(
      `dataTablePerformance.dimension.customer_movement_type_detailed_key.${sanitizeValueToI18nKey(props.cell.data)}`
    );
  }

  if (props.columnId === 'date_key_evolution_week' && props.cell.data !== 'Total') {
    return t('dataTablePerformance.dimension.value.date_key_evolution_week', {
      weekNumber: props.cell.data
    });
  }
  return null;
});

const isCustomerSegmentationDimensionColumn = computed(() => isCustomerSegmentationColumn(props.columnId));
</script>

<template>
  <ContentCellContainer v-bind="$props" :type="cell.type">
    <div
      v-if="cell.type === 'dimension' && isCustomerSegmentationDimensionColumn"
      class="font-bold px-6 w-max max-w-72"
    >
      <span class="line-clamp-2" :title="cell.data ?? ''">
        {{ cell.data ?? '-' }}
      </span>
    </div>
    <p
      v-else-if="cell.type === 'dimension'"
      class="font-bold px-6 w-max max-w-72 whitespace-pre-line"
      :class="dimensionScoreClass"
    >
      <span
        :class="dimensionSpanClass"
        class="rounded-md m-2 p-1 line-clamp-3"
        :title="formattedDimensionsTextValues ?? cell.data ?? ''"
      >
        {{ formattedDimensionsTextValues ?? cell.data ?? '-' }}
      </span>
    </p>
    <div v-else class="px-4 ml-auto w-max" :class="isEmpty ? 'text-center' : 'text-right'">
      <span :title="cell.rawData">{{ cell.data ?? '-' }}</span>
      <div
        v-if="cell.variationScore !== undefined || cell.variationScoreBenchmark !== undefined"
        class="flex justify-end items-center mt-1 font-bold text-xs text-[#696969] gap-2"
      >
        <span v-if="cell.variationScore" :class="variationScoreClass" :title="cell.rawVariationScore">{{
          cell.variationScore ?? '-'
        }}</span>
        <hr v-if="cell.variationScore && cell.variationScoreBenchmark" class="h-4 w-px bg-[#EBEBEB]" />
        <div
          v-if="cell.variationScoreBenchmark"
          class="flex items-center gap-1"
          :title="cell.rawVariationScoreBenchmark"
        >
          <component :is="cell.variationScoreBenchmarkIcon" class="text-[#696969] fill-[#696969] w-5" />
          <span>{{ cell.variationScoreBenchmark ?? '-' }}</span>
        </div>
      </div>
    </div>
  </ContentCellContainer>
</template>
